// IANA timezone -> ISO 3166-1 alpha-2 country.
//
// The browser knows its timezone with no network call, which makes it a better
// country signal for us than a server-side IP lookup: it reflects where the user
// actually is, not where their VPN or corporate proxy exits. Only zones that map
// to exactly one country are listed — an ambiguous zone is better left unknown
// than silently wrong.

const TZ_COUNTRY: Record<string, string> = {
  // Europe
  'Europe/Dublin': 'IE', 'Europe/London': 'GB', 'Europe/Lisbon': 'PT', 'Europe/Madrid': 'ES',
  'Europe/Paris': 'FR', 'Europe/Brussels': 'BE', 'Europe/Amsterdam': 'NL', 'Europe/Luxembourg': 'LU',
  'Europe/Berlin': 'DE', 'Europe/Zurich': 'CH', 'Europe/Vienna': 'AT', 'Europe/Rome': 'IT',
  'Europe/Copenhagen': 'DK', 'Europe/Oslo': 'NO', 'Europe/Stockholm': 'SE', 'Europe/Helsinki': 'FI',
  'Europe/Warsaw': 'PL', 'Europe/Prague': 'CZ', 'Europe/Bratislava': 'SK', 'Europe/Budapest': 'HU',
  'Europe/Bucharest': 'RO', 'Europe/Sofia': 'BG', 'Europe/Athens': 'GR', 'Europe/Istanbul': 'TR',
  'Europe/Kyiv': 'UA', 'Europe/Kiev': 'UA', 'Europe/Moscow': 'RU', 'Europe/Tallinn': 'EE',
  'Europe/Riga': 'LV', 'Europe/Vilnius': 'LT', 'Europe/Zagreb': 'HR', 'Europe/Belgrade': 'RS',
  'Europe/Ljubljana': 'SI', 'Europe/Sarajevo': 'BA', 'Europe/Skopje': 'MK', 'Europe/Tirane': 'AL',
  'Europe/Malta': 'MT', 'Europe/Reykjavik': 'IS', 'Atlantic/Reykjavik': 'IS',

  // North America — US/Canada zones are country-unambiguous even though multiple exist
  'America/New_York': 'US', 'America/Detroit': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
  'America/Phoenix': 'US', 'America/Los_Angeles': 'US', 'America/Anchorage': 'US', 'Pacific/Honolulu': 'US',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Edmonton': 'CA', 'America/Winnipeg': 'CA',
  'America/Halifax': 'CA', 'America/St_Johns': 'CA', 'America/Regina': 'CA',
  'America/Mexico_City': 'MX', 'America/Monterrey': 'MX', 'America/Tijuana': 'MX',

  // South America
  'America/Sao_Paulo': 'BR', 'America/Bahia': 'BR', 'America/Fortaleza': 'BR', 'America/Manaus': 'BR',
  'America/Argentina/Buenos_Aires': 'AR', 'America/Santiago': 'CL', 'America/Bogota': 'CO',
  'America/Lima': 'PE', 'America/Caracas': 'VE', 'America/Montevideo': 'UY', 'America/Asuncion': 'PY',
  'America/La_Paz': 'BO', 'America/Guayaquil': 'EC', 'America/Panama': 'PA', 'America/Costa_Rica': 'CR',
  'America/Guatemala': 'GT', 'America/Havana': 'CU', 'America/Santo_Domingo': 'DO', 'America/Jamaica': 'JM',
  'America/Puerto_Rico': 'PR',

  // Asia
  'Asia/Jerusalem': 'IL', 'Asia/Tel_Aviv': 'IL', 'Asia/Beirut': 'LB', 'Asia/Damascus': 'SY',
  'Asia/Amman': 'JO', 'Asia/Baghdad': 'IQ', 'Asia/Riyadh': 'SA', 'Asia/Kuwait': 'KW',
  'Asia/Qatar': 'QA', 'Asia/Bahrain': 'BH', 'Asia/Dubai': 'AE', 'Asia/Muscat': 'OM',
  'Asia/Tehran': 'IR', 'Asia/Karachi': 'PK', 'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
  'Asia/Colombo': 'LK', 'Asia/Dhaka': 'BD', 'Asia/Kathmandu': 'NP', 'Asia/Yangon': 'MM',
  'Asia/Bangkok': 'TH', 'Asia/Ho_Chi_Minh': 'VN', 'Asia/Saigon': 'VN', 'Asia/Phnom_Penh': 'KH',
  'Asia/Vientiane': 'LA', 'Asia/Kuala_Lumpur': 'MY', 'Asia/Singapore': 'SG', 'Asia/Jakarta': 'ID',
  'Asia/Makassar': 'ID', 'Asia/Manila': 'PH', 'Asia/Hong_Kong': 'HK', 'Asia/Macau': 'MO',
  'Asia/Taipei': 'TW', 'Asia/Shanghai': 'CN', 'Asia/Urumqi': 'CN', 'Asia/Seoul': 'KR',
  'Asia/Tokyo': 'JP', 'Asia/Ulaanbaatar': 'MN', 'Asia/Almaty': 'KZ', 'Asia/Tashkent': 'UZ',
  'Asia/Baku': 'AZ', 'Asia/Tbilisi': 'GE', 'Asia/Yerevan': 'AM',

  // Africa
  'Africa/Cairo': 'EG', 'Africa/Tripoli': 'LY', 'Africa/Tunis': 'TN', 'Africa/Algiers': 'DZ',
  'Africa/Casablanca': 'MA', 'Africa/Lagos': 'NG', 'Africa/Accra': 'GH', 'Africa/Abidjan': 'CI',
  'Africa/Dakar': 'SN', 'Africa/Nairobi': 'KE', 'Africa/Kampala': 'UG', 'Africa/Dar_es_Salaam': 'TZ',
  'Africa/Addis_Ababa': 'ET', 'Africa/Khartoum': 'SD', 'Africa/Johannesburg': 'ZA',
  'Africa/Harare': 'ZW', 'Africa/Lusaka': 'ZM', 'Africa/Maputo': 'MZ', 'Africa/Luanda': 'AO',
  'Africa/Kinshasa': 'CD', 'Africa/Douala': 'CM',

  // Oceania
  'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU',
  'Australia/Perth': 'AU', 'Australia/Adelaide': 'AU', 'Australia/Hobart': 'AU',
  'Australia/Darwin': 'AU', 'Pacific/Auckland': 'NZ', 'Pacific/Fiji': 'FJ',
  'Pacific/Port_Moresby': 'PG', 'Pacific/Guam': 'GU',
};

// The visitor's country from their browser timezone, or undefined when the zone is
// unknown/ambiguous or the Intl API is unavailable.
export const countryFromTimezone = (): string | undefined => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz ? TZ_COUNTRY[tz] : undefined;
  } catch {
    return undefined;
  }
};

export const __TZ_COUNTRY_FOR_TESTS = TZ_COUNTRY;
